import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import type { AsyncData } from 'nuxt/app'

interface UseAsyncStateParams<T> {
  key: string
  fetcher: () => Promise<T>
  fallback: T
}

interface UseAsyncStateResult<T> {
  state: Ref<T>
  pending: ComputedRef<boolean>
  error: AsyncData<T, unknown>['error']
  refresh: AsyncData<T, unknown>['refresh']
  ready: () => Promise<void>
}

export const useAsyncState = <T>({ key, fetcher, fallback }: UseAsyncStateParams<T>): UseAsyncStateResult<T> => {
  const asyncData = useAsyncData<T, unknown>(key, fetcher)
  const state = ref(fallback) as Ref<T>

  watch(
    () => asyncData.data.value,
    (value) => {
      state.value = (value ?? fallback) as T
    },
    { immediate: true },
  )

  const ready = async () => {
    await asyncData
  }

  return {
    state,
    // AsyncData.pending is deprecated in Nuxt 4; derive from status
    pending: computed(() => asyncData.status.value === 'pending'),
    error: asyncData.error,
    refresh: asyncData.refresh,
    ready,
  }
}
